import { useEffect, useState, type ReactNode } from "react";
import { RotateCwIcon } from "lucide-react";
import SetupChecksPanel from "@component/setup-checks-panel";
import CountdownTimer from "@component/countdown-timer";
import { PermissionModal } from "@component/media-permission-warning";
import type { AudioLevelStatus } from "@component/audio-level-indicator";

// Same delay used inside AudioLevelIndicator — wait this long before flipping
// the mic row from green to a warning so we don't blip during the analyser
// warmup window right after device switch / initial mount.
const LOW_AUDIO_WARNING_DELAY_MS = 3000;

interface WaitingRoomSetupChecksProps {
    cameraGranted: boolean;
    micGranted: boolean;
    onCameraGranted: () => void;
    onMicGranted: () => void;
    onPermissionError?: (msg: string) => void;
    /**
     * Live connection quality, owned by the parent so the existing
     * non-setup-checks consumers (e.g. the video panel) keep reading from
     * the same source. The panel computes the row state from this prop.
     */
    connectionQuality: "good" | "poor";
    /**
     * Live audio level status from AudioLevelIndicator. When `micGranted` is
     * true but this stays "low" for a few seconds, surface a non-blocking
     * warning on the mic row — catches users who picked a virtual-loopback
     * input device (e.g. LoomAudioDevice) that produces silence as a
     * capture source.
     */
    audioLevelStatus?: AudioLevelStatus;
    /**
     * True when the currently-selected mic is a known virtual / loopback
     * device (detected by label, e.g. "LoomAudioDevice"). Used together
     * with `audioLevelStatus` to keep the warning stable — without this,
     * virtual mics that occasionally pick up system audio would cause the
     * warning to flicker on/off as the audio level oscillates.
     */
    isVirtualMicSelected?: boolean;
    interviewLanguage?: string;
    /** True once Time-to-join row should appear in the panel. */
    showTimeToJoin?: boolean;
    timeoutMinutes?: number;
    onTimeout?: () => void;
    /**
     * Forwarded to PermissionModal — when set, the "Report this issue" link in
     * the modal triggers this callback (so the parent can open its existing
     * IssuesDialog with its own userId/assessmentId context).
     */
    onReportIssue?: () => void;
}

export default function WaitingRoomSetupChecks({
    cameraGranted,
    micGranted,
    onCameraGranted,
    onMicGranted,
    onPermissionError,
    connectionQuality,
    audioLevelStatus,
    isVirtualMicSelected = false,
    interviewLanguage = "en-US",
    showTimeToJoin = false,
    timeoutMinutes,
    onTimeout,
    onReportIssue,
}: WaitingRoomSetupChecksProps) {
    // Setup-checks-only signals from the Network Information API — kept local
    // since only the network row needs the human-readable subtitle.
    const [downlink, setDownlink] = useState<number | null>(null);
    const [rtt, setRtt] = useState<number | null>(null);
    const [isOnline, setIsOnline] = useState<boolean>(typeof navigator !== "undefined" ? navigator.onLine : true);

    // When permission is already "denied", browsers will not re-show the
    // native prompt — JS can't force it. Fall back to a browser-specific
    // instructions modal so the user can re-enable from site settings.
    const [helpModalFor, setHelpModalFor] = useState<"camera" | "microphone" | null>(null);

    // True once mic is granted AND audio level has been "low" continuously
    // for LOW_AUDIO_WARNING_DELAY_MS. Drives the non-blocking warning state
    // on the mic row for users who selected a non-capturing input.
    const [lowAudioWarning, setLowAudioWarning] = useState(false);
    useEffect(() => {
        if (!micGranted || audioLevelStatus !== "low") {
            setLowAudioWarning(false);
            return;
        }
        const timer = setTimeout(() => {
            setLowAudioWarning(true);
        }, LOW_AUDIO_WARNING_DELAY_MS);
        return () => clearTimeout(timer);
    }, [audioLevelStatus, micGranted]);

    // Either trigger surfaces the mic warning. Virtual-mic detection is
    // label-based (stable), so combining it here prevents the audio-level
    // check from flickering when a virtual mic temporarily picks up system
    // sound (AI greeting, browser audio, background music in another tab).
    const showMicWarning = micGranted && (lowAudioWarning || isVirtualMicSelected);

    const readNetwork = () => {
        // @ts-expect-error Navigator connection API is not in lib.dom yet
        const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        if (c) {
            setDownlink(typeof c.downlink === "number" ? c.downlink : null);
            setRtt(typeof c.rtt === "number" ? c.rtt : null);
        } else {
            setDownlink(null);
            setRtt(null);
        }
        setIsOnline(navigator.onLine);
    };

    useEffect(() => {
        readNetwork();
        // @ts-expect-error Navigator connection API is not in lib.dom yet
        const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        c?.addEventListener?.("change", readNetwork);
        window.addEventListener("online", readNetwork);
        window.addEventListener("offline", readNetwork);
        return () => {
            c?.removeEventListener?.("change", readNetwork);
            window.removeEventListener("online", readNetwork);
            window.removeEventListener("offline", readNetwork);
        };
    }, []);

    const isMalay = interviewLanguage === "Malay";
    const isIndonesian = interviewLanguage === "id-ID";
    const t = (en: string, id: string, ms: string): string => {
        if (isMalay) return ms;
        if (isIndonesian) return id;
        return en;
    };

    // Per-device permission prompts triggered from the panel.
    //   - If the Permissions API reports "denied" → open the instructions
    //     modal directly (the browser will not show a native prompt anymore).
    //   - Otherwise → call getUserMedia; the native prompt surfaces for
    //     "prompt" state, and granted state just succeeds quietly.
    //   - If getUserMedia rejects with NotAllowedError (Permissions API not
    //     supported, e.g. older Safari), fall through to the modal anyway.
    const probePermission = async (kind: "camera" | "microphone") => {
        try {
            // @ts-expect-error 'camera'/'microphone' valid PermissionName in browsers but not TS lib yet
            const status = await navigator.permissions?.query?.({ name: kind });
            if (status?.state === "denied") {
                setHelpModalFor(kind);
                return;
            }
        } catch {
            // Permissions API not supported — fall through to getUserMedia.
        }
        try {
            const constraints: MediaStreamConstraints = kind === "camera" ? { video: true } : { audio: true };
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            stream.getTracks().forEach((tr) => tr.stop());
            if (kind === "camera") onCameraGranted();
            else onMicGranted();
        } catch (err) {
            if (err instanceof Error && err.name === "NotAllowedError") {
                setHelpModalFor(kind);
                onPermissionError?.(
                    kind === "camera"
                        ? "Permission denied. Please grant access to camera."
                        : "Permission denied. Please grant access to microphone."
                );
            }
        }
    };
    const requestCamera = () => probePermission("camera");
    const requestMic = () => probePermission("microphone");

    // Three network states:
    //   - offline      → red X (treat like a permission failure)
    //   - poor (slow)  → yellow warning + "Test again" button
    //   - good         → green check
    const networkState: "ok" | "warning" | "error" = !isOnline
        ? "error"
        : connectionQuality === "good"
            ? "ok"
            : "warning";

    const networkTitle = networkState === "error"
        ? t("No internet", "Tidak ada internet", "Tiada internet")
        : networkState === "warning"
            ? t("Network okay", "Jaringan cukup", "Rangkaian okay")
            : t("Network excellent", "Jaringan sangat baik", "Rangkaian sangat baik");

    const networkSubtitle = (() => {
        if (networkState === "error") {
            return t("Check your internet connection.", "Periksa koneksi internet Anda.", "Semak sambungan internet anda.");
        }
        // navigator.connection.downlink is a quantized RTT-based estimate
        // that frequently reports implausibly low values (0.1-0.5 Mbps) on
        // healthy broadband links during quiet periods. Suppress the number
        // when it's below 1 Mbps so the subtitle doesn't say "0.3 Mbps,
        // low latency" — better to show no number than a misleading one.
        const mbps = downlink != null && downlink >= 1 ? `${downlink.toFixed(1)} Mbps` : null;
        if (networkState === "warning") {
            const hint = t("Try a stronger signal spot.", "Coba pindah ke sinyal yang lebih kuat.", "Cuba tempat dengan isyarat lebih kuat.");
            return mbps ? `${mbps}. ${hint}` : hint;
        }
        // good
        const latency = rtt != null
            ? (rtt < 100
                ? t("low latency", "latensi rendah", "kependaman rendah")
                : t("high latency", "latensi tinggi", "kependaman tinggi"))
            : null;
        if (mbps && latency) return `${mbps}, ${latency}`;
        if (mbps) return mbps;
        return t("You're online.", "Anda terhubung.", "Anda dalam talian.");
    })();

    return (
        <>
            <SetupChecksPanel
                title={t("Setup checks", "Pemeriksaan persiapan", "Semakan persediaan")}
                rows={[
                    {
                        key: "mic",
                        // Three states: granted+audible (green), granted+silent (yellow
                        // warning, non-blocking), no permission (red error). Warning
                        // is advisory — SetupChecksPanel ignores it in "to fix" count.
                        ok: micGranted && !showMicWarning,
                        variant: showMicWarning ? "warning" : "error",
                        title: !micGranted
                            ? t("Microphone permission needed", "Izin mikrofon diperlukan", "Kebenaran mikrofon diperlukan")
                            : showMicWarning
                                ? t("No audio detected", "Tidak ada audio terdeteksi", "Tiada audio dikesan")
                                : t("Microphone enabled", "Mikrofon aktif", "Mikrofon diaktifkan"),
                        subtitle: !micGranted
                            ? t("Allow mic access to hear you.", "Izinkan akses mikrofon agar dapat mendengar Anda.", "Benarkan akses mikrofon untuk mendengar anda.")
                            : showMicWarning
                                ? t(
                                    "Try selecting a different microphone.",
                                    "Coba pilih mikrofon yang berbeda.",
                                    "Cuba pilih mikrofon yang lain.",
                                )
                                : t("We can hear you clearly.", "Suara Anda terdengar jelas.", "Suara anda kedengaran jelas."),
                        // Only the no-permission state gets the Enable button —
                        // for the warning state the candidate fixes it by
                        // picking a different mic from the dropdown above.
                        onEnable: !micGranted ? requestMic : undefined,
                        enableLabel: !micGranted ? t("Enable", "Aktifkan", "Aktifkan") : undefined,
                    },
                    {
                        key: "camera",
                        ok: cameraGranted,
                        title: cameraGranted
                            ? t("Camera enabled", "Kamera aktif", "Kamera diaktifkan")
                            : t("Camera permission needed", "Izin kamera diperlukan", "Kebenaran kamera diperlukan"),
                        subtitle: cameraGranted
                            ? t("We can see you clearly.", "Wajah Anda terlihat jelas.", "Wajah anda kelihatan jelas.")
                            : t("Allow camera access to verify you.", "Izinkan akses kamera untuk verifikasi.", "Benarkan akses kamera untuk pengesahan."),
                        onEnable: requestCamera,
                        enableLabel: t("Enable", "Aktifkan", "Aktifkan"),
                    },
                    {
                        key: "network",
                        ok: networkState === "ok",
                        variant: networkState === "warning" ? "warning" : "error",
                        title: networkTitle,
                        subtitle: networkSubtitle,
                        // Only the warning state gets a button — offline has no
                        // re-test recourse beyond the user fixing their connection.
                        onEnable: networkState === "warning" ? readNetwork : undefined,
                        enableLabel: networkState === "warning" ? (
                            <>
                                <span className="hidden sm:block">{t("Test again", "Tes ulang", "Uji semula")}</span>
                                <RotateCwIcon className="h-3 w-3" strokeWidth={2.5} />
                            </>
                        ) : undefined,
                    },
                ]}
                timeToJoinLabel={t("Time to join", "Waktu untuk bergabung", "Masa untuk menyertai")}
                timeToJoinElement={
                    showTimeToJoin && timeoutMinutes != null && onTimeout
                        ? (
                            <CountdownTimer
                                initialMinutes={timeoutMinutes}
                                onTimeout={onTimeout}
                                className="inline-block"
                            />
                        ) as ReactNode
                        : undefined
                }
            />
            <PermissionModal
                isOpen={helpModalFor !== null}
                onClose={() => setHelpModalFor(null)}
                permLower={helpModalFor ?? "camera"}
                permTitle={helpModalFor === "microphone" ? "Microphone" : "Camera"}
                onReportIssue={onReportIssue}
            />
        </>
    );
}
